import { useCallback } from 'react'

import {
    ClinicFieldsFragment,
    namedOperations,
    useAddAiAgentInstructionForClinicMutation,
} from '~graphql/generated/graphql'
import { useToastFeedback } from '~utils/hooks/use-toast-feedback'
import { parseInstructionsString } from '~components/settings/engagement/ai-receptionist-settings/helpers'

interface UseTeachAgentOptions {
    clinic: ClinicFieldsFragment
    /** Fired after a successful append, useful for clearing local form state. */
    onSuccess?: () => void
}

interface UseTeachAgentResult {
    /**
     * Teach the AI Receptionist one instruction. The server saves it wherever
     * this clinic's agent reads it: the call playbook's Clinic details for
     * Single Prompt receptionists, Custom Instructions otherwise. Fires
     * `onSuccess` (and a toast) on success; shows an error toast otherwise.
     * No-ops on empty/whitespace input.
     */
    addInstruction: (instruction: string) => void
    /** True while the mutation is in flight. */
    isSaving: boolean
    /** Current Custom Instructions (parsed from the clinic settings). */
    currentInstructions: string[]
}

/** Encapsulates the "Teach the Agent" flow used from the call QA modal. */
export function useTeachAgent({
    clinic,
    onSuccess,
}: UseTeachAgentOptions): UseTeachAgentResult {
    const toasts = useToastFeedback(
        'Added to your AI Receptionist instructions',
        'Failed to add instruction. Please try again.',
    )

    const currentInstructions = parseInstructionsString(
        clinic.settings?.ai_agent_custom_instructions,
    )

    const [addAgentInstruction, { loading }] =
        useAddAiAgentInstructionForClinicMutation({
            refetchQueries: [
                namedOperations.Query.getClinic,
                namedOperations.Query.getAiAgentPromptForClinic,
            ],
            awaitRefetchQueries: true,
            onCompleted: () => {
                toasts.onCompleted()
                onSuccess?.()
            },
            onError: error => toasts.onError(error.message),
        })

    const addInstruction = useCallback(
        (instruction: string): void => {
            const trimmed = instruction.trim()
            if (!trimmed) return

            // Fire-and-forget: success/error feedback is wired through the
            // mutation's onCompleted/onError callbacks above, so we don't
            // double-handle the promise here.
            void addAgentInstruction({
                variables: { clinicId: clinic.id, instruction: trimmed },
            })
        },
        [addAgentInstruction, clinic.id],
    )

    return {
        addInstruction,
        isSaving: loading,
        currentInstructions,
    }
}
